import clsx from "clsx";
import type { ReactNode } from "react";
import { SEVERITY_COLOR, SEVERITY_LABEL } from "../../lib/format";
import type { Severity } from "../../types/api";

export function GlassCard({
  children,
  className,
  edge,
}: {
  children: ReactNode;
  className?: string;
  edge?: Severity;
}) {
  return (
    <div className={clsx("glass p-4", edge && `edge-${edge}`, className)}>{children}</div>
  );
}

export function SectionTag({ children }: { children: ReactNode }) {
  return <div className="section-tag mb-2">{children}</div>;
}

export function StatCard({
  label,
  value,
  sub,
  accent,
  critical,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  accent?: string;
  critical?: boolean;
}) {
  return (
    <GlassCard className="relative overflow-hidden">
      <div className="section-tag">{label}</div>
      <div
        className="mt-1 text-3xl font-bold"
        style={{ color: critical ? "var(--critical)" : accent ?? "var(--ink)" }}
      >
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-slatey">{sub}</div>}
      {critical && (
        <span className="absolute right-3 top-3 h-2 w-2 rounded-full bg-critical animate-pulseDot" />
      )}
    </GlassCard>
  );
}

export function SeverityCapsule({ severity }: { severity: Severity }) {
  return (
    <span
      className="mono inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
      style={{
        color: SEVERITY_COLOR[severity],
        background: `${SEVERITY_COLOR[severity]}1f`,
        border: `1px solid ${SEVERITY_COLOR[severity]}55`,
      }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: SEVERITY_COLOR[severity] }} />
      {SEVERITY_LABEL[severity]}
    </span>
  );
}

export function Pill({
  children,
  color,
  className,
}: {
  children: ReactNode;
  color?: string;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        "mono inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider",
        className
      )}
      style={{
        color: color ?? "var(--slate)",
        background: color ? `${color}14` : "rgba(148,163,184,0.1)",
        border: `1px solid ${color ?? "rgba(148,163,184,0.25)"}40`,
      }}
    >
      {children}
    </span>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slatey">
      <span className="h-3 w-3 animate-spin rounded-full border-2 border-aurora-cyan border-t-transparent" />
      {label ?? "Loading…"}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full min-h-[120px] items-center justify-center rounded-glass border border-dashed border-slate-700/40 text-sm text-slatey">
      {children}
    </div>
  );
}

export function Button({
  children,
  onClick,
  variant = "primary",
  disabled,
  className,
  type = "button",
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "ghost" | "danger" | "cyan";
  disabled?: boolean;
  className?: string;
  type?: "button" | "submit";
}) {
  const styles: Record<string, string> = {
    primary: "bg-pacific/90 hover:bg-pacific text-white",
    cyan: "bg-aurora-cyan/15 hover:bg-aurora-cyan/25 text-aurora-cyan border border-aurora-cyan/40",
    ghost: "bg-white/5 hover:bg-white/10 text-ink border border-white/10",
    danger: "bg-critical/15 hover:bg-critical/25 text-critical border border-critical/40",
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        "rounded-lg px-3 py-1.5 text-sm font-medium transition disabled:opacity-40",
        styles[variant],
        className
      )}
    >
      {children}
    </button>
  );
}
